import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import { isInAppHref } from '@src/routing/href';
import './AccountChip.css';

export interface AccountChipProps {
  /** Glyph in front of the label — a person for the account, a gift for a game. */
  icon: ReactNode;
  children: ReactNode;
  /** Makes the chip a link. Without it the chip is a plain label. */
  href?: string;
  /**
   * true — only the icon. `narrow` — only the icon below 48rem, so a label
   * still fits on a wide header.
   */
  labelHidden?: boolean | 'narrow';
  /** Read out when the label is hidden. */
  label?: string;
  testId?: string;
}

/**
 * Header chip naming the signed-in user, or the game a screen belongs to. It is
 * deliberately not a Tag: a Tag is a filter, this is state.
 */
export function AccountChip({
  icon,
  children,
  href,
  labelHidden = false,
  label,
  testId,
}: AccountChipProps) {
  const body = (
    <>
      <span className="account-chip__icon" aria-hidden="true">
        {icon}
      </span>
      <Typography as="span" role="label/nav" color="inherit" className="account-chip__label">
        {children}
      </Typography>
    </>
  );

  const className = classes(
    'account-chip',
    labelHidden === true && 'account-chip--icon-only',
    labelHidden === 'narrow' && 'account-chip--icon-narrow',
  );

  if (href) {
    if (isInAppHref(href)) {
      return (
        <RouterLink className={className} to={href} aria-label={label} {...testIdAttr(testId)}>
          {body}
        </RouterLink>
      );
    }
    return (
      <a className={className} href={href} aria-label={label} {...testIdAttr(testId)}>
        {body}
      </a>
    );
  }

  return (
    <span className={className} aria-label={label} {...testIdAttr(testId)}>
      {body}
    </span>
  );
}
